/* AF - offers progress bar (.af-offers in assets/af-ui.css, markup in
   snippets/af-offers.liquid). Shared by the product page, quick add and the
   cart drawer (the last two use the compact bar: short offer lines, no note).
   The summary sentence (.af-offers__line-text) is for screen readers only;
   what shows is one line per offer.

   Offers come from two places:
   - assets/af-promos.json: the real automatic discounts (e.g. 10% over ৳4,000)
   - data-af-offers-extra on the bar: display-only offers the store runs
     outside Shopify discounts (currently empty).
   Offers don't stack; the bar shows how far the basket is from each one.

   API:
     AFOffers.render(el, basketCents)  draw the bar for this basket
                                       (loads the offers on first use)
   The caller decides what "basket" means: the PDP and quick add use cart
   subtotal + the selected size x quantity; the cart drawer uses the cart
   subtotal.

   First paint is drawn in Liquid (snippets/af-offers.liquid) from a copy of
   the same offers; data-af-offers-key says which. When af-promos.json
   changes, update that copy too, or the bar redraws once after load. */
(() => {
  if (window.AFOffers) return;

  const TTL = 5 * 60 * 1000;
  const cache = new Map(); // url -> { at, promise }

  const fmt = (taka) => '৳' + Math.round(taka).toLocaleString('en-US');

  function loadPromos(url) {
    if (!url) return Promise.resolve([]);
    const hit = cache.get(url);
    if (hit && Date.now() - hit.at < TTL) return hit.promise;
    const promise = fetch(url, { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : { promos: [] }))
      .then((d) => (Array.isArray(d && d.promos) ? d.promos : []))
      .catch(() => []);
    cache.set(url, { at: Date.now(), promise });
    return promise;
  }

  function active(p) {
    if (!p || p.active === false) return false;
    const now = new Date().toISOString();
    if (p.starts_at && now < p.starts_at) return false;
    if (p.ends_at && now > p.ends_at) return false;
    return Number(p.min_subtotal) > 0;
  }

  // af-promos.json entry -> { title, cond, min, pct }
  function fromPromo(p) {
    const d = p.discount || {};
    const min = Number(p.min_subtotal);
    let title = p.title || '';
    let pct = 0;
    if (d.kind === 'percent' && Number(d.value) > 0) {
      pct = Number(d.value);
      title = `${pct}% off`;
    } else if (d.kind === 'fixed' && Number(d.value) > 0) {
      title = `${fmt(d.value)} off`;
    }
    return { title, cond: `orders over ${fmt(min)}`, min, pct };
  }

  function extras(el) {
    try {
      const list = JSON.parse(el.getAttribute('data-af-offers-extra') || '[]');
      return list.filter((o) => o && o.title && Number(o.min) > 0).map((o) => ({ title: o.title, cond: o.cond || '', min: Number(o.min), pct: 0 }));
    } catch (e) {
      return [];
    }
  }

  // same drawing as the 'tag' case in snippets/af-icon.liquid
  const ICON =
    '<svg class="af-ic af-ic--tag" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path class="af-ic__fill" d="M3.5 12.1V5a1.5 1.5 0 0 1 1.5-1.5h7.1a1.5 1.5 0 0 1 1.06.44l7.4 7.4a1.5 1.5 0 0 1 0 2.12l-7.1 7.1a1.5 1.5 0 0 1-2.12 0l-7.4-7.4a1.5 1.5 0 0 1-.44-1.06z"/><circle cx="8.2" cy="8.2" r="1.4"/></svg>';

  function build(el, offers) {
    const track = el.querySelector('.af-offers__track');
    const rows = el.querySelector('.af-offers__rows');
    const compact = el.classList.contains('af-offers--compact');
    const top = Math.max(...offers.map((o) => o.min));
    track.querySelectorAll('.af-offers__dot').forEach((n) => n.remove());
    if (rows) rows.textContent = '';
    offers.forEach((o) => {
      const dot = document.createElement('span');
      dot.className = 'af-offers__dot';
      dot.style.left = `${(o.min / top) * 100}%`;
      track.appendChild(dot);
      if (!rows) return;

      const li = document.createElement('li');
      li.className = 'af-offers__row';
      const mark = document.createElement('span');
      mark.className = 'af-offers__mark';
      mark.innerHTML = ICON;
      const text = document.createElement('span');
      text.className = 'af-offers__text';
      if (compact) {
        text.textContent = o.title;
      } else {
        // product page: the offer's name, its condition on the line below
        const b = document.createElement('b');
        b.className = 'af-offers__name';
        b.textContent = o.title;
        text.appendChild(b);
        if (o.cond) {
          const c = document.createElement('span');
          c.className = 'af-offers__cond';
          c.textContent = o.cond;
          text.append(' ', c);
        }
      }
      const status = document.createElement('span');
      status.className = 'af-offers__status';
      li.append(mark, text, status);
      rows.appendChild(li);
    });
  }

  function paint(el, offers, basket) {
    const top = Math.max(...offers.map((o) => o.min));
    const ok = offers.filter((o) => basket >= o.min);
    const next = offers.find((o) => basket < o.min);
    const best = ok[ok.length - 1];
    const bestTxt = best
      ? best.pct
        ? `${best.title} unlocked · you save ${fmt((basket * best.pct) / 100)}`
        : `${best.title} unlocked`
      : '';
    let line;
    if (next) line = best ? `${bestTxt}. ${fmt(next.min - basket)} more for ${next.title}` : `${fmt(next.min - basket)} away from ${next.title}`;
    else line = bestTxt;

    const lineEl = el.querySelector('.af-offers__line-text');
    if (lineEl && lineEl.textContent !== line) lineEl.textContent = line;
    const countEl = el.querySelector('.af-offers__count');
    if (countEl) countEl.textContent = `${ok.length}/${offers.length} unlocked`;
    const fill = el.querySelector('.af-offers__fill');
    if (fill) fill.style.width = `${Math.min(100, (basket / top) * 100).toFixed(1)}%`;
    const dots = el.querySelectorAll('.af-offers__dot');
    const rows = el.querySelectorAll('.af-offers__row');
    offers.forEach((o, i) => {
      const on = basket >= o.min;
      if (dots[i]) dots[i].classList.toggle('is-on', on);
      if (rows[i]) {
        rows[i].classList.toggle('is-on', on);
        const s = rows[i].querySelector('.af-offers__status');
        const txt = on ? (o.pct ? `Unlocked · save ${fmt((basket * o.pct) / 100)}` : 'Unlocked') : `${fmt(o.min - basket)} to go`;
        if (s && s.textContent !== txt) s.textContent = txt;
      }
    });
  }

  const state = new WeakMap(); // el -> { offers, key }

  async function render(el, basketCents) {
    if (!el) return;
    let st = state.get(el);
    if (!st) {
      st = { offers: null, pending: null, basket: 0 };
      state.set(el, st);
    }
    st.basket = Math.max(0, Number(basketCents) || 0) / 100;
    if (!st.offers) {
      if (!st.pending) {
        st.pending = loadPromos(el.getAttribute('data-promos-url')).then((promos) => {
          const list = extras(el).concat(promos.filter(active).map(fromPromo)).sort((a, b) => a.min - b.min);
          st.offers = list;
          if (!list.length) {
            el.hidden = true;
            return;
          }
          // The snippet already drew the bar for the offers it knows about,
          // so first paint doesn't jump. Rebuild only if the list differs.
          const key = list.map((o) => `${o.title}@${o.min}`).join('|');
          if (el.getAttribute('data-af-offers-key') !== key) {
            build(el, list);
            el.setAttribute('data-af-offers-key', key);
          }
        });
      }
      await st.pending;
      if (!st.offers || !st.offers.length) return;
    }
    paint(el, st.offers, st.basket);
  }

  window.AFOffers = { render, format: fmt };
})();
